import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import { LoadingSection } from "../../components/Spinner";

type Lead = {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  subject?: string | null;
  message: string;
  type: string;
  gym?: string | null;
  createdAt: string;
  read: boolean;
};

interface MailConfig {
  contactTo: string | null;
  mailerConfigured: boolean;
}

export function AdminLeads() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "leads"],
    queryFn: () => api<Lead[]>("/admin/leads"),
  });

  const { data: mailConfig } = useQuery({
    queryKey: ["admin", "mail-config"],
    queryFn: () => api<MailConfig>("/admin/mail-config"),
  });

  const markRead = useMutation({
    mutationFn: (id: string) =>
      api(`/admin/leads/${id}`, { method: "PATCH", json: { read: true } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "leads"] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/admin/leads/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "leads"] }),
  });

  const handleDelete = (lead: Lead) => {
    const ok = window.confirm(
      `Supprimer le message de ${lead.name} (${lead.email}) ? Cette action est irréversible.`,
    );
    if (ok) remove.mutate(lead.id);
  };

  return (
    <div>
      <h1 className="font-display text-3xl font-bold uppercase">Messages</h1>

      {/* Notifications email (lecture seule, configurées côté serveur) */}
      <div className="mt-6 card space-y-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-ink-700">
              Adresse de secours des demandes
            </p>
            <span className="inline-flex items-center gap-1 rounded-full bg-ink-100 px-2 py-0.5 text-[11px] font-semibold text-ink-500">
              🔒 Lecture seule
            </span>
          </div>
          <p className="mt-1 text-xs text-ink-500">
            Reçoit les demandes du formulaire de contact quand aucune salle précise n'est
            sélectionnée (« Peu importe »). Lorsqu'une salle est choisie, ce sont les
            « Emails de réception » de cette salle (onglet <strong>Salles</strong>) qui sont
            utilisés. Cette adresse est définie dans la configuration serveur et n'est pas
            modifiable ici.
          </p>
          <div className="mt-2">
            <code className="inline-block rounded bg-ink-100 px-2 py-1 text-sm">
              {mailConfig
                ? mailConfig.contactTo ?? "— aucune adresse de secours configurée —"
                : "…"}
            </code>
          </div>
        </div>

        {mailConfig && !mailConfig.mailerConfigured && (
          <p className="rounded border border-brand-200 bg-brand-50 p-3 text-xs text-brand-600">
            ⚠ Aucun serveur SMTP n'est configuré : les emails de notification ne sont pas
            envoyés (les demandes restent visibles ci-dessous).
          </p>
        )}
      </div>

      <div className="mt-6 space-y-3">
        {isLoading && <LoadingSection />}
        {data?.map((l) => (
          <details
            key={l.id}
            className={`card ${l.read ? "opacity-70" : "border-brand-500"}`}
            onToggle={(e) => {
              if ((e.target as HTMLDetailsElement).open && !l.read) markRead.mutate(l.id);
            }}
          >
            <summary className="cursor-pointer list-none">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold">
                    {l.name} <span className="text-xs text-ink-500">· {l.email}</span>
                  </div>
                  <div className="text-sm text-ink-500">{l.subject ?? l.type}</div>
                </div>
                <div className="text-xs text-ink-500">
                  {new Date(l.createdAt).toLocaleString("fr-FR")}
                </div>
              </div>
            </summary>
            <div className="mt-4 border-t border-ink-100 pt-4 text-sm">
              <p className="whitespace-pre-wrap">{l.message}</p>
              {l.phone && <p className="mt-2 text-ink-500">📞 {l.phone}</p>}
              {l.gym && <p className="text-ink-500">Salle : {l.gym}</p>}
              <button
                onClick={() => handleDelete(l)}
                className="mt-4 text-xs text-brand-500 hover:underline"
              >
                Supprimer
              </button>
            </div>
          </details>
        ))}
        {data?.length === 0 && <p className="text-ink-500">Aucun message pour l'instant.</p>}
      </div>
    </div>
  );
}
